import { useState, useEffect, useRef, useMemo } from 'react';
import { Loader2, Check, X, Download, Search, Receipt, ClipboardList } from 'lucide-react';
import type { OrgMember } from './memberTypes';
import { ExportDateInput } from './ExportDateInput';

// The three org-scoped CSV download panels on the Manager Exports tab (#343).
// Pure presentation: every value and every handler arrives as a prop from
// useExportWorkflows. The frozen labels (Fill/accounting CSV, Settlements CSV,
// Order audit CSV) are mandated by docs/csv-export-contracts.md — see #122/#133.

const EXPORT_PRESET_OPTIONS = [
  { label: 'All', days: null },
  { label: '7D', days: 7 },
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
  { label: '365D', days: 365 },
] as const;

export function OrgTradeExportPanel({
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  onPreset,
  accounts,
  selectedTraderIds,
  onSelectedTraderIdsChange,
  onExport,
  busy,
  exporting,
  message,
  exportError,
  className = 'mb-6',
}: {
  fromDate: string;
  toDate: string;
  onFromDateChange: (value: string) => void;
  onToDateChange: (value: string) => void;
  onPreset: (days: number | null) => void;
  accounts: OrgMember[];
  selectedTraderIds: string[];
  onSelectedTraderIdsChange: (value: string[]) => void;
  onExport: () => void;
  busy: boolean;
  exporting: boolean;
  message: string | null;
  exportError?: string | null;
  className?: string;
}) {
  const [traderQuery, setTraderQuery] = useState('');
  const [traderMenuOpen, setTraderMenuOpen] = useState(false);
  const [activeAccountId, setActiveAccountId] = useState<string | null>(null);
  const activeAccountOptionRef = useRef<HTMLButtonElement | null>(null);
  const accountById = useMemo(() => new Map(accounts.map((account) => [account.id, account])), [accounts]);
  const selectedTraderIdSet = useMemo(() => new Set(selectedTraderIds), [selectedTraderIds]);
  const selectedAccounts = selectedTraderIds
    .map((traderId) => accountById.get(traderId))
    .filter((account): account is OrgMember => Boolean(account));
  const filteredAccounts = useMemo(() => {
    const query = traderQuery.trim().toLowerCase();
    return accounts.filter((account) => account.username.toLowerCase().includes(query));
  }, [accounts, traderQuery]);
  const activeAccountIndex = activeAccountId
    ? filteredAccounts.findIndex((account) => account.id === activeAccountId)
    : -1;

  useEffect(() => {
    if (!traderMenuOpen) return;
    if (filteredAccounts.length === 0) {
      if (activeAccountId !== null) setActiveAccountId(null);
      return;
    }
    if (activeAccountIndex === -1) {
      setActiveAccountId(filteredAccounts[0].id);
    }
  }, [activeAccountId, activeAccountIndex, filteredAccounts, traderMenuOpen]);

  useEffect(() => {
    if (!traderMenuOpen) return;
    activeAccountOptionRef.current?.scrollIntoView({ block: 'nearest' });
  }, [activeAccountId, traderMenuOpen]);

  const firstMatchingAccountId = (query: string) => (
    accounts.find((account) =>
      account.username.toLowerCase().includes(query.trim().toLowerCase()),
    )?.id ?? null
  );

  const toggleTrader = (traderId: string) => {
    onSelectedTraderIdsChange(
      selectedTraderIdSet.has(traderId)
        ? selectedTraderIds.filter((id) => id !== traderId)
        : [...selectedTraderIds, traderId],
    );
    setActiveAccountId(traderId);
  };
  const removeTrader = (traderId: string) => {
    onSelectedTraderIdsChange(selectedTraderIds.filter((id) => id !== traderId));
  };
  const removeLatestTrader = () => {
    const latestTraderId = selectedTraderIds.at(-1);
    if (!latestTraderId) return;
    removeTrader(latestTraderId);
  };
  const updateTraderQuery = (query: string) => {
    setTraderQuery(query);
    setTraderMenuOpen(true);
    setActiveAccountId(firstMatchingAccountId(query));
  };
  const openTraderMenu = () => {
    setTraderMenuOpen(true);
    if (activeAccountIndex === -1) {
      setActiveAccountId(filteredAccounts[0]?.id ?? null);
    }
  };
  const handleTraderKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && traderQuery.length === 0 && selectedTraderIds.length > 0) {
      event.preventDefault();
      removeLatestTrader();
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setTraderMenuOpen(true);
      if (filteredAccounts.length === 0) return;
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      const nextIndex = activeAccountIndex === -1
        ? (direction === 1 ? 0 : filteredAccounts.length - 1)
        : (activeAccountIndex + direction + filteredAccounts.length) % filteredAccounts.length;
      setActiveAccountId(filteredAccounts[nextIndex].id);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      setTraderMenuOpen(true);
      const activeAccount = activeAccountIndex === -1
        ? filteredAccounts[0]
        : filteredAccounts[activeAccountIndex];
      if (activeAccount) {
        toggleTrader(activeAccount.id);
      }
      return;
    }

    if (event.key === 'Escape' && traderMenuOpen) {
      event.preventDefault();
      setTraderMenuOpen(false);
      setTraderQuery('');
    }
  };

  return (
    <section className={`bg-card border-border rounded-lg border p-4 ${className}`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Download size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-semibold">Fill/accounting CSV</h3>
        </div>
        <button
          onClick={onExport}
          disabled={busy}
          title={exportError ? 'Retry org fill/accounting CSV export' : 'Export org fill/accounting CSV'}
          className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50"
        >
          {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
          {exportError && !exporting ? 'Retry export' : 'Fill/accounting CSV'}
        </button>
      </div>

      <div className="space-y-3">
        <div className="flex flex-wrap items-end gap-2">
          <ExportDateInput
            id="org-export-from"
            label="From (leave blank for all history)"
            value={fromDate}
            onChange={onFromDateChange}
            disabled={busy}
          />
          <ExportDateInput
            id="org-export-to"
            label="To (leave blank for up to now)"
            value={toDate}
            onChange={onToDateChange}
            disabled={busy}
          />
          <div className="flex flex-wrap gap-1.5 pb-0.5">
            {EXPORT_PRESET_OPTIONS.map((option) => (
              <button
                key={option.label}
                type="button"
                onClick={() => onPreset(option.days)}
                disabled={busy}
                className="bg-secondary text-muted-foreground hover:text-foreground rounded-md px-2 py-1 text-[11px] font-medium disabled:opacity-50"
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div
          className="relative"
          onBlur={() => window.setTimeout(() => {
            setTraderMenuOpen(false);
            setTraderQuery('');
          }, 120)}
        >
          <label className="text-muted-foreground mb-1 block text-[11px] font-medium" htmlFor="org-export-traders">
            Traders (leave blank for all)
          </label>
          <div className="bg-background border-border flex min-h-9 flex-wrap items-center gap-1 rounded-md border px-2 py-1 focus-within:border-blue-500">
            {selectedAccounts.map((account) => (
              <span
                key={account.id}
                className="bg-blue-500/15 text-blue-600 dark:text-blue-400 inline-flex max-w-full items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium"
              >
                <span className="truncate">{account.username}</span>
                <button
                  type="button"
                  onClick={() => removeTrader(account.id)}
                  disabled={busy}
                  title={`Remove ${account.username}`}
                  className="rounded p-0.5 hover:bg-blue-500/10 disabled:opacity-50"
                >
                  <X size={10} />
                </button>
              </span>
            ))}
            <Search size={13} className="text-muted-foreground shrink-0" />
            <input
              id="org-export-traders"
              value={traderQuery}
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={traderMenuOpen}
              aria-controls="org-export-traders-listbox"
              aria-activedescendant={traderMenuOpen && activeAccountId ? `org-export-account-${activeAccountId}` : undefined}
              onChange={(e) => updateTraderQuery(e.target.value)}
              onFocus={openTraderMenu}
              onKeyDown={handleTraderKeyDown}
              disabled={busy}
              placeholder={selectedAccounts.length > 0 ? 'Add trader' : 'All traders'}
              className="text-foreground placeholder:text-muted-foreground min-w-24 flex-1 bg-transparent py-1 text-xs focus:outline-none disabled:opacity-50"
            />
          </div>
          {traderMenuOpen && (
            <div
              id="org-export-traders-listbox"
              role="listbox"
              aria-multiselectable="true"
              className="bg-card border-border absolute z-20 mt-1 max-h-52 w-full overflow-auto rounded-md border shadow-lg"
            >
              {filteredAccounts.length === 0 ? (
                <div className="text-muted-foreground px-3 py-2 text-xs">No traders</div>
              ) : (
                filteredAccounts.map((account) => {
                  const selected = selectedTraderIdSet.has(account.id);
                  const active = activeAccountId === account.id;
                  return (
                    <button
                      key={account.id}
                      id={`org-export-account-${account.id}`}
                      ref={active ? activeAccountOptionRef : undefined}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseEnter={() => setActiveAccountId(account.id)}
                      onClick={() => toggleTrader(account.id)}
                      className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs ${
                        active ? 'bg-muted/70 text-foreground' : 'hover:bg-muted/60'
                      }`}
                    >
                      <span className="min-w-0 truncate">{account.username}</span>
                      <span className="text-muted-foreground ml-auto shrink-0 text-[10px] uppercase">
                        {account.role}
                      </span>
                      <Check
                        size={13}
                        className={selected ? 'text-blue-500 opacity-100' : 'text-muted-foreground opacity-0'}
                      />
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
      <div aria-live="polite">
        {message && (
          <div className="mt-3 rounded-md bg-emerald-500/10 px-3 py-2 text-xs text-emerald-600 dark:text-emerald-400">
            {message}
          </div>
        )}
        {exportError && (
          <div className="mt-3 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">
            {exportError} — click Retry export to try again.
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * Org-scoped Settlements CSV download (#122). Presentation only: it calls the
 * frozen #113 endpoint and hands the bytes straight to the browser.
 *
 * Deliberately has NO date range, unlike the neighbouring Fill/accounting panel.
 * This export is a CURRENT position snapshot: an open position carries a null
 * `settled_at`, so any `from`/`to` the endpoint accepts silently drops every
 * open position from the file (see docs/csv-export-contracts.md — "a settled_at
 * range filters the current state; it does not reconstruct past state"). The
 * per-trader route refuses dates outright for the same reason. A date-ranged,
 * multi-trader settlements view with a preview table already exists in Cash
 * Ledger Preview; this panel is the plain org-scope download Manager was missing.
 */
export function OrgSettlementExportPanel({
  onExport,
  exporting,
  message,
  exportError,
  className = 'mb-6',
}: {
  onExport: () => void;
  exporting: boolean;
  message: string | null;
  exportError?: string | null;
  className?: string;
}) {
  return (
    <section className={`bg-card border-border rounded-lg border p-4 ${className}`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Receipt size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-semibold">Settlements CSV</h3>
        </div>
        <button
          onClick={onExport}
          disabled={exporting}
          title={exportError ? 'Retry org Settlements CSV export' : 'Export org Settlements CSV'}
          className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50"
        >
          {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
          {exportError && !exporting ? 'Retry export' : 'Settlements CSV'}
        </button>
      </div>

      <div className="text-muted-foreground space-y-2 text-[11px] leading-relaxed">
        <p>
          Whole organization · Polymarket only · current snapshot, no date range.
        </p>
        <p>
          One row per position with shares remaining — open or settled — carrying cost
          basis, settlement P&amp;L, realized fees and the settled win/loss result.
        </p>
        <p>
          <span className="text-foreground font-medium">Not the same file as:</span>{' '}
          Fill/accounting CSV (one row per executed fill), Order audit CSV (order actions),
          or the wallet Activity / cash ledger (wallet cash movements, not position accounting).
        </p>
        <p>
          A position that was sold off completely has no row here — its economics live in the
          Fill/accounting CSV. The two files are alternative views and are not additive.
        </p>
      </div>

      <div aria-live="polite">
        {message && (
          <div className="mt-3 rounded-md bg-emerald-500/10 px-3 py-2 text-xs text-emerald-600 dark:text-emerald-400">
            {message}
          </div>
        )}
        {exportError && (
          <div className="mt-3 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">
            {exportError} — click Retry export to try again.
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * Org-scoped Order audit CSV download (#133). Presentation only: it calls the
 * frozen #109 endpoint and hands the bytes straight to the browser.
 *
 * Has no controls of its own by design. #133 says to reuse the organization,
 * selected-trader, venue and date filters #109 already supports, so the range
 * and trader selection come from the neighbouring Fill/accounting panel's
 * controls (the same reading that settled the equivalent ambiguity on #122).
 * Venue has no control on this tab and the endpoint reads an absent venue as
 * `all`, exactly as the Fill/accounting export already does.
 *
 * The copy avoids "above" / "to the left": the Exports grid reflows to one
 * column below `xl`, so any directional word would be false at some width.
 */
export function OrgOrderAuditExportPanel({
  onExport,
  exporting,
  message,
  exportError,
  className = 'mb-6',
}: {
  onExport: () => void;
  exporting: boolean;
  message: string | null;
  exportError?: string | null;
  className?: string;
}) {
  return (
    <section className={`bg-card border-border rounded-lg border p-4 ${className}`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ClipboardList size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-semibold">Order audit CSV</h3>
        </div>
        <button
          onClick={onExport}
          disabled={exporting}
          title={exportError ? 'Retry org Order audit CSV export' : 'Export org Order audit CSV'}
          className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50"
        >
          {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
          {exportError && !exporting ? 'Retry export' : 'Order audit CSV'}
        </button>
      </div>

      <div className="text-muted-foreground space-y-2 text-[11px] leading-relaxed">
        <p>
          Whole organization or the selected traders · both venues · uses the same date
          range and trader selection as the Fill/accounting panel.
        </p>
        <p>
          One row per order <span className="text-foreground font-medium">action attempt</span> —
          including attempts the venue rejected, with the error message and the raw
          request/response payloads kept for forensics.
        </p>
        <p>
          <span className="text-foreground font-medium">Not the same file as:</span>{' '}
          Fill/accounting CSV (one row per executed fill), Settlements CSV
          (current position snapshot), Market tape CSV (venue data, not
          account-attributed), or the wallet Activity / cash ledger.
        </p>
      </div>

      <div aria-live="polite">
        {message && (
          <div className="mt-3 rounded-md bg-emerald-500/10 px-3 py-2 text-xs text-emerald-600 dark:text-emerald-400">
            {message}
          </div>
        )}
        {exportError && (
          <div className="mt-3 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">
            {exportError} — click Retry export to try again.
          </div>
        )}
      </div>
    </section>
  );
}
